import React from 'react';
import { useUserStore } from '../../stores/userStore';
import { useUserStats } from '../../hooks/useUserStats';
import { useTheme } from '../../hooks/useTheme';

interface AvatarProgressRingProps {
  size?: number;
  onClick?: () => void;
  /** Draw the progress ring around the avatar (ring/both modes). */
  showRing?: boolean;
  /** Show the level badge overlapping the avatar's bottom edge. */
  showLevel?: boolean;
  /** Button title/aria-label — the header uses it as the notification trigger. */
  title?: string;
  ariaLabel?: string;
}

/**
 * Level badge text (numbers only):
 *  - level 0    → "0"   (1 digit)
 *  - level 1–99 → "01"…"99" (2 digits, zero-padded)
 *  - level 100+ → "100"… (3+ digits, as-is)
 */
function formatLevel(level: number): string {
  const n = Number.isFinite(level) ? Math.max(0, Math.floor(level)) : 0;
  if (n === 0) return '0';
  if (n < 100) return String(n).padStart(2, '0');
  return String(n);
}

/**
 * Header avatar (mobile). Optionally wraps the avatar in a live level-progress
 * ring, and overlays a compact level badge on the avatar's bottom edge. The
 * mobile counterpart to the desktop rail's UserProgress.
 */
export function AvatarProgressRing({
  size = 40,
  onClick,
  showRing = true,
  showLevel = true,
  title = 'View Profile',
  ariaLabel,
}: AvatarProgressRingProps) {
  const { name, initials, avatarUrl } = useUserStore();
  const { stats } = useUserStats();
  const { primaryColorCSS: primaryColor, isMetanoia } = useTheme();

  /**
   * ⭐⭐ THIS IS THE RAIL'S COLLAPSED AVATAR, TO THE PIXEL (owner, 2026-08-23).
   *
   * The rail already renders a 40px avatar-with-progress that reads correctly,
   * so this stopped being redesigned and started being COPIED. Compared against
   * `UserProgress`'s `isCollapsed` branch, every value here now matches it:
   * `strokeWidth` 2, `radius = (size - strokeWidth) / 2`, the avatar inset by
   * 4px (`inset-1`), the same faint track, and the SVG painted BEFORE the
   * avatar so the picture sits on top of the ring.
   *
   * ⚠️⚠️ THE ONLY THING THAT EVER DIFFERED WAS THE STROKE — 3px here against
   * the rail's 2px, at the same 40px diameter. That is what made a low-progress
   * arc read as a fat blob beside the avatar rather than a thin ring around it.
   * Four previous attempts moved the avatar's size and the ring's radius around
   * instead, and none of them could work, because neither was the difference.
   *
   * ⚠️ If you change one of these, change the rail's to match, or they drift
   * apart again — they are the same control at the same size on one screen.
   */
  const strokeWidth = 2;
  const AVATAR_INSET = 4;                    // `inset-1`, as the rail uses
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = Math.min(Math.max(isNaN(stats.progress) ? 0 : stats.progress, 0), 100);
  const progressOffset = circumference - (progress / 100) * circumference;

  return (
    <button
      onClick={onClick}
      className="relative shrink-0 group"
      style={{ width: size, height: size }}
      title={title}
      aria-label={ariaLabel ?? `View profile — level ${stats.level}${showRing ? `, ${progress}% to next level` : ''}`}
    >

      {showRing && (
        <svg className="absolute inset-0 w-full h-full -rotate-90">
          {isMetanoia && (
            <defs>
              <linearGradient id="metanoia-grad-header" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#3CC2DD" />
                <stop offset="50%" stopColor="#A739E2" />
                <stop offset="100%" stopColor="#F58C56" />
              </linearGradient>
            </defs>
          )}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            /* ⚠️ MEASURED against the BUILT stylesheet, not the dev server: the
               track is `--border` = rgb(40,40,40) and the page is rgb(11,11,11),
               so `opacity-30` composited to luminance 19.7 — 8.7 points of
               contrast out of 255, which is why a short arc read as a lone cap
               with no circle behind it. `opacity-60` doubles that to ~17.4.
               ⚠️ `UserProgress` carries the same two tracks — change both or the
               header and the rail stop matching. */
            className="text-border opacity-60"
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={progressOffset}
            strokeLinecap="round"
            stroke={isMetanoia ? 'url(#metanoia-grad-header)' : primaryColor}
            className="transition-all duration-500 drop-shadow-sm"
          />
        </svg>
      )}

      {/* ⚠️ The flat `ring-primary` hover reads as CYAN, which is only one stop
          of the brand gradient — on the Metanoia theme it contradicted the
          gradient the progress ring above it is already drawing. The shared
          `accent-gradient-border-hover` utility is inert off the brand theme, so
          other themes keep the flat ring. */}
      <span
        style={{ inset: AVATAR_INSET }}
        className={`absolute rounded-full overflow-hidden bg-muted flex items-center justify-center transition-all ${
        isMetanoia ? 'accent-gradient-border-hover' : 'group-hover:ring-2 group-hover:ring-primary/60'
      }`}>
        {avatarUrl ? (
          <img src={avatarUrl} alt={name} className="w-full h-full object-cover" />
        ) : (
          <span className="text-xs font-semibold text-muted-foreground">{initials}</span>
        )}
      </span>


      {/* Level badge — overlaps the avatar's bottom edge, sitting on top. */}
      {showLevel && (
        <span className="absolute left-1/2 -bottom-1 z-10 -translate-x-1/2 min-w-[15px] rounded-full bg-primary px-1 py-px text-center text-[9px] font-bold leading-none tabular-nums text-primary-foreground ring-2 ring-card">
          {formatLevel(stats.level)}
        </span>
      )}
    </button>
  );
}
